import type { ReactNode } from 'react';
import React, { Children, cloneElement } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Collapse, UnmountClosed } from 'react-collapse';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc';
import { getTabsBackgroundClasses } from 'Components/utils/tabs/tabs.util';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { ARROW_DOWN, ARROW_UP, END, HOME } from 'Utils/keyCodes';
import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes';
import type { IconName } from 'Components/frameworks/icon/Icon';
import 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TabHeader } from 'Components/frameworks/tab-header/TabHeader';
type AccordionItem = {
  header: {
    color?: Colors;
    iconName?: IconName;
    title: ReactNode;
  };
  headerId: string;
  panelId: string;
  content: ReactNode;
};
type AccordionProps = {
  areAllCollapsable?: boolean;
  areMultiExpandable?: boolean;
  className?: string;
  containerSize?: 'mini' | 'small' | 'medium' | 'large';
  hasBackground?: boolean;
  hasNoAutoScroll?: boolean;
  headerLevel?: number;
  id?: string;
  initialSelectedItems?: Array<number>;
  isContentUnmountedOnCollapse?: boolean;
  items: Array<AccordionItem>;
  onSelect?: (index: number) => void;
  scrollOffsetSelector?: string;
  theme?: 'default';
};

const scrollToId = (id: string, offsetSelector: string) =>
  scrollToElement(`#${id}`, 100, `.${offsetSelector}`, false);

const _Accordion = ({
  areAllCollapsable,
  areMultiExpandable,
  className,
  containerSize,
  hasBackground,
  hasNoAutoScroll,
  headerLevel = 3,
  initialSelectedItems = [],
  id,
  isContentUnmountedOnCollapse,
  items,
  onSelect,
  scrollOffsetSelector = NAVIGATION_OFFSET_SELECTOR,
  theme = 'default',
}: AccordionProps) => {
  const { isTablet } = useResponsive();

  if (!items.length) {
    return null;
  }

  const refsArray: Array<HTMLElement | null> = [];

  const onKeyDown = ({ e, index }: { e: React.KeyboardEvent; index: number }) => {
    if (items.length > 1) {
      const lastItem = items.length - 1;
      let idx = -1;

      switch (e.which) {
        case ARROW_DOWN:
          e.preventDefault();
          idx = index === lastItem ? 0 : index + 1;
          break;

        case ARROW_UP:
          e.preventDefault();
          idx = index === 0 ? lastItem : index - 1;
          break;

        case HOME:
          e.preventDefault();
          idx = 0;
          break;

        case END:
          e.preventDefault();
          idx = lastItem;
          break;

        default:
      }

      refsArray[idx] && refsArray[idx].focus();
    }
  };

  const CollapsedTag = isContentUnmountedOnCollapse ? UnmountClosed : Collapse;
  const accordionsCount = items.filter(Boolean).length;
  return (
    <ActiveIndexes
      activeIndexes={initialSelectedItems}
      hasMultipleIndexEnabled={areMultiExpandable}
      hasNoIndexEnabled={areAllCollapsable}
      onUpdate={onSelect}
    >
      {({ update, activeIndexes, isPristine }) => (
        <section
          className={className}
          id={id}
          data-cs-override-id="Accordion" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
        >
          {items.filter(Boolean).map(({ content, header = {}, headerId, panelId }, index) => {
            const { title, ...rest } = header;
            const isExpanded = activeIndexes.includes(index);

            const onRest = () => {
              if (!hasNoAutoScroll && isExpanded && !isPristine) {
                scrollToId(headerId, scrollOffsetSelector);
              }
            };

            const Heading = `h${headerLevel}` as keyof JSX.IntrinsicElements;
            return (
              <article key={headerId}>
                <SiteContainer size={containerSize}>
                  <Heading>
                    <TabHeader
                      {...rest}
                      className="w-full"
                      controlsId={panelId}
                      id={headerId}
                      isAccordion
                      isActive={isExpanded}
                      isDisabled={accordionsCount <= 1 && isTablet}
                      onClick={() => {
                        update(index);
                        refsArray[index] && refsArray[index]?.focus();
                      }}
                      onKeyDown={(e) =>
                        onKeyDown({
                          e,
                          index,
                        })
                      }
                      ref={(ref: HTMLElement | null) => {
                        refsArray[index] = ref;
                      }}
                      theme={theme}
                    >
                      {title}
                    </TabHeader>
                  </Heading>
                </SiteContainer>
                <CollapsedTag isOpened={isExpanded} onRest={onRest}>
                  <div
                    aria-hidden={!isExpanded}
                    aria-labelledby={headerId}
                    className={classnames(getTabsBackgroundClasses(hasBackground), {
                      'pointer-events-none invisible': !isExpanded,
                    })}
                    data-cs-override-id="Accordion-panel" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
                    id={panelId}
                    role="region"
                  >
                    <SiteContainer size={containerSize}>
                      {Children.map(content, (el, i) =>
                        React.isValidElement(el)
                          ? cloneElement(el as React.ReactElement, {
                              key: `${panelId}-${i}`,
                            })
                          : el,
                      )}
                    </SiteContainer>
                  </div>
                </CollapsedTag>
              </article>
            );
          })}
        </section>
      )}
    </ActiveIndexes>
  );
};

export const Accordion = withOpenAndScrollToHash(_Accordion);
